/**
 * The active card's neighbourhood: the card, the cards wired to it and those wires. The rest of the
 * canvas fades, and the lit wires are drawn above the cards, so a wire that passes behind another
 * card can be followed end to end.
 */

import type { Edge, Node } from '@xyflow/react';
import type { WireData } from './flow-layout';

interface Neighbourhood {
	nodes: Set<string>;
	edges: Set<string>;
}

// Above the cards, which React Flow draws at z-index 0.
const RAISED = 1;

const FADED_NODE = 'opacity-30 transition-opacity';
const FADED_LINE = 'opacity-10';

export const neighbourhoodOf = (nodeId: string, edges: Edge[]): Neighbourhood => {
	const touching = edges.filter((edge) => edge.source === nodeId || edge.target === nodeId);
	return {
		nodes: new Set([nodeId, ...touching.flatMap((edge) => [edge.source, edge.target])]),
		edges: new Set(touching.map((edge) => edge.id))
	};
};

export const withNodeEmphasis = (nodes: Node[], around: Neighbourhood | null): Node[] =>
	nodes.map((node) => ({ ...node, className: around && !around.nodes.has(node.id) ? FADED_NODE : 'transition-opacity' }));

/** Wires say how they are drawn through their data; the plain lines into the result fade by class. */
export const withEdgeEmphasis = (edges: Edge[], around: Neighbourhood | null): Edge[] =>
	edges.map((edge) => {
		if (!around) return edge;
		const lit = around.edges.has(edge.id);
		if (edge.type !== 'wire') return lit ? { ...edge, zIndex: RAISED } : { ...edge, className: FADED_LINE };
		const data = { ...(edge.data as WireData), emphasis: lit ? 'lit' : 'faded' } satisfies WireData;
		// Raised over the cards, a wire takes clicks only on its own stroke, so controls under it still work.
		return lit ? { ...edge, data, zIndex: RAISED, interactionWidth: 0 } : { ...edge, data };
	});
